// Demande de complément à l'usager : choix des pièces manquantes ou non conformes, message libre ;
// le motif transmis (visible par l'usager, notifié) rassemble les pièces et le message. La
// transition suspend les délais jusqu'à la réception du complément.
import type { DossierDetail, TransitionDisponible } from '@cc/contrats/ui-metier';
import { Bandeau, Bouton, CaseACocher, DialogueConfirmation, ZoneTexte } from '@cc/ui';
import { useState } from 'react';
import { requete } from '../../../api/client';
import { useAction } from '../../../commun/useAction';
import styles from '../metier.module.css';

/** Motif envoyé à l'usager : pièces attendues puis message. */
export function composerMotifComplement(pieces: string[], message: string): string {
  const parties: string[] = [];
  if (pieces.length > 0) parties.push(`Pièces à fournir : ${pieces.join(', ')}.`);
  if (message.trim()) parties.push(message.trim());
  return parties.join(' ');
}

export function DemandeComplement({ dossier: d, transition, invalider }: { dossier: DossierDetail; transition: TransitionDisponible; invalider: (readonly unknown[])[] }) {
  const [ouvert, setOuvert] = useState(false);
  const [choisies, setChoisies] = useState<string[]>([]);
  const [message, setMessage] = useState('');
  const fournis = new Map(d.pieces.map((p) => [p.type_piece, p]));
  const propositions = d.pieces_attendues.map((p) => {
    const fournie = fournis.get(p.type);
    const etat = !fournie ? 'manquante' : fournie.verdict === 'non_conforme' ? `non conforme${fournie.motif ? ` (${fournie.motif})` : ''}` : fournie.verdict === 'conforme' ? 'conforme' : 'non examinée';
    return { type: p.type, libelle: p.libelle, etat, aCocher: !fournie || fournie.verdict === 'non_conforme' };
  });
  const motif = composerMotifComplement(propositions.filter((p) => choisies.includes(p.type)).map((p) => p.libelle), message);
  const demande = useAction({
    executer: () => requete(`/dossiers/${d.id}/transitions/${transition.code}`, { methode: 'POST', corps: { motif } }),
    succes: 'Complément demandé : l\'usager est informé et les délais sont suspendus.',
    invalider,
    apres: () => {
      setOuvert(false);
      setChoisies([]);
      setMessage('');
    },
  });
  return (
    <>
      <Bouton variante="secondaire" onClick={() => { setChoisies(propositions.filter((p) => p.aCocher).map((p) => p.type)); setOuvert(true); }}>
        Demander un complément
      </Bouton>
      <DialogueConfirmation
        ouvert={ouvert}
        titre="Demander un complément à l'usager"
        libelleConfirmer="Envoyer la demande"
        variante="secondaire"
        enCours={demande.enCours}
        confirmationDesactivee={motif.trim().length < 3}
        onConfirmer={() => demande.lancer(undefined)}
        onAnnuler={() => setOuvert(false)}
        message={<p>Le dossier passe à « Complément demandé » ; les délais sont suspendus jusqu'à la réception. L'usager reçoit ce message sur ses canaux et dans son suivi.</p>}
      >
        {propositions.length > 0 ? (
          <fieldset className={styles.pile} style={{ border: 0, padding: 0, margin: 0 }}>
            <legend className="cc-etiquette">Pièces à demander</legend>
            {propositions.map((p) => (
              <CaseACocher key={p.type} libelle={`${p.libelle} (${p.etat})`} checked={choisies.includes(p.type)} onChange={(e) => setChoisies(e.target.checked ? [...choisies, p.type] : choisies.filter((x) => x !== p.type))} />
            ))}
          </fieldset>
        ) : null}
        <ZoneTexte libelle="Message à l'usager" value={message} rows={3} required={choisies.length === 0} facultatif={choisies.length > 0} aide="Précisez ce qui manque ou ce qui doit être corrigé." onChange={(e) => setMessage(e.target.value)} />
        <p className="cc-etiquette">Texte transmis</p>
        <p>{motif || 'Choisissez au moins une pièce ou écrivez un message.'}</p>
        {demande.messageErreur ? <Bandeau type="erreur">{demande.messageErreur}</Bandeau> : null}
      </DialogueConfirmation>
    </>
  );
}
